import { CommentEntity } from 'mk2/apps/blogs/schemas';
import { TestingStatus } from 'mk2/apps/bo/schemas';
import { AST, ASTAdditionalDataFromServer } from 'mk2/apps/wiki/schemas';
import { createLikeableSchema, LikeableEntity } from 'mk2/containers/Like/Like.schemas';
import { DeletedByRole } from 'mk2/helpers/deleteable';
import { Entity, PhotoEntity, PhotoSchema, UserEntity, UserSchema } from 'mk2/schemas';
import { schema } from 'normalizr';

export type PageRef = number | 'last';

export interface CategoryEntity extends Entity {
    orderIndex: number;
    name: string;
    slug: string;
    coverPhoto: PhotoEntity;
    id: number;
}

export interface TopicModeratorEntity extends Entity {
    user?: UserEntity;
    topic?: TopicEntity;
    createdTime: string;
}

export interface TopicEntity extends Entity {
    slug: string;
    category: CategoryEntity;
    name: string;
    description: string;
    // Deprecated - use topicModerators.map((tm) => tm.user)
    moderators: UserEntity[];
    topicModerators: TopicModeratorEntity[];
    coverPhoto: PhotoEntity;
    isCommercial: boolean;
    isBrandDays: boolean;
    deletedAt: string;
    deletedReason: string;
    deletedBy: UserEntity;
    numRecentContributors: number;
    numThreads: number;
    visibility: number;
    created: string;
}

export interface TopicModeratorInvitationEntity extends Entity {
    invitee: UserEntity;
    inviter: UserEntity;
    createdAt: string;
    deleteAt: string;
    acceptedAt: string;
}

export interface TopicModeratorsDetailsEntity extends Entity {
    allModerators: Array<{
        user: UserEntity;
        createdAt: string;
        deletedAt: string;
    }>;
    pendingInvitations: TopicModeratorInvitationEntity[];
}

export interface ProductTestingEntity extends Entity {
    name: string;
    isActive: boolean;
    trackedHashtag: string;
    status: TestingStatus;
    teaserTitle: string;
    teaserText: AST;
    teaserLink: string;
    teaserASTAdditionalData: ASTAdditionalDataFromServer;
    callTitle: string;
    callText: AST;
    callLink: string;
    callEndDate: string;
    callASTAdditionalData: ASTAdditionalDataFromServer;
    inProgressTitle: string;
    inProgressText: AST;
    inProgressASTAdditionalData: ASTAdditionalDataFromServer;
    finishedTitle: string;
    finishedText: AST;
    finishedTesters: string;
    finishedASTAdditionalData: ASTAdditionalDataFromServer;
    photos: PhotoEntity[];
    finishedTextProductTestingPhotos: ProductTestingPhotoEntity[];
    updatedTime: string;
    user: UserEntity;
    testersPostsCount?: number;
}

export interface MessagePhotoEntity extends Entity {
    photofile: PhotoEntity;
    message?: CommentEntity;
    position: number;
}

export interface PostEntity extends Entity {
    topic: TopicEntity;
    author: UserEntity;
    created: string;
    heading: string;
    perexAST: AST;
    perexASTAdditionalData: ASTAdditionalDataFromServer;
    // Deprecated - use messagePhotos
    photos: PhotoEntity[];
    messagePhotos: MessagePhotoEntity[];
    stickerId: number;
    likeable: LikeableEntity;
    comments: number;
    sticky: boolean;
    locked: boolean;
    lastMessageTime: string;
    slug: string;
    popularAddedOn: string;
    noindex: boolean;
    isNoAds: boolean;
    isHoax: boolean;
    canBeDeleted: boolean;
    deletedAt?: string;
    deletedByRole?: DeletedByRole;
    deletedReason?: string;
    deletedBy?: UserEntity;
    counsellingAdminApprovedTime: string;
    testing?: ProductTestingEntity;
    contestEnded?: boolean;
}

export interface MyAnonymousPostEntity extends Entity {}

export enum ContestType {
    Article = 'article',
    Forum = 'forum',
    ShortMessage = 'message',
}

export interface ContestEntity extends Entity {
    name: string;
    isActive: boolean;
    isDraft: boolean;
    endDate: string;
    seeAnswers: boolean;
    postId: number;
    blogPostId: number;
    photo: PhotoEntity;
    replyCount: number;
    title: string;
    type: ContestType;
    url: string;
}

export interface BrandDaysCampaignEntity extends Entity {
    name: string;
    title: string;
    user: UserEntity;
    logoUrl: string; // kam ma smerovat preklik z loga
    logoSrc: string; // url kde je obrazok
    logoWidth: number; // rozmery loga (dlzka), zobrazi sa do obdlznika 80x30
    logoHeight: number; // rozmery loga (sirka), zobrazi sa do obdlznika 80x30
    logoPrefix: string; // text co sa zobrazi pred logom
    forumTopicSlug: string;
}

export interface CounsellingEntity extends Entity {
    counsellor: UserEntity;
    counsellorAvatar: string;
    counsellorAvatarPhoto: PhotoEntity;
    counsellorName: string;
    counsellorSignatureAST: AST;
    isLocked: boolean;
    isArchived: boolean;
    lockedMessage: string;
    topic: TopicEntity;
}

export interface ArchivedCampaignsEntity extends Entity {
    topic: TopicEntity;
    date: Date;
    isActive: boolean;
}

export interface ProductTestingPhotoEntity extends Entity {
    productTesting: ProductTestingEntity;
    photofile: PhotoEntity;
    position: number;
    deletedAt: string;
}

export const CategorySchema = new schema.Entity('forum.category', {
    coverPhoto: PhotoSchema,
});

export const TopicSchema = new schema.Entity('forum.topic', {});

export const TopicModeratorSchema = new schema.Entity('forum.topicmoderator', {
    user: UserSchema,
    topic: TopicSchema,
});

TopicSchema.define({
    category: CategorySchema,
    moderators: [UserSchema],
    coverPhoto: PhotoSchema,
    deletedBy: UserSchema,
    topicModerators: [TopicModeratorSchema],
});

export const TopicModeratorInvitationSchema = new schema.Entity('forum.topicmoderatorinvitation', {
    invitee: UserSchema,
    inviter: UserSchema,
});

export const TopicModeratorsDetailsSchema = new schema.Entity('topicModeratorsDetails', {
    allModerators: [
        new schema.Object({
            user: UserSchema,
        }),
    ],
    pendingInvitations: [TopicModeratorInvitationSchema],
});

export const ProductTestingPhotoSchema = new schema.Entity('photoblog.producttestingphoto', {
    photofile: PhotoSchema,
});

export const ProductTestingSchema = new schema.Entity('photoblog.producttesting', {
    photos: [PhotoSchema],
    finishedTextProductTestingPhotos: [ProductTestingPhotoSchema],
    user: UserSchema,
});

export const MessagePhotoSchema = new schema.Entity('forum.messagephoto', {});

export const PostSchema = new schema.Entity('forum.thread', {
    topic: TopicSchema,
    author: UserSchema,
    photos: [PhotoSchema],
    messagePhotos: [MessagePhotoSchema],
    likeable: createLikeableSchema('thread'),
    deletedBy: UserSchema,
    testing: ProductTestingSchema,
});

export const MyAnonymousPostSchema = new schema.Entity('myAnonymousPosts');

export const ContestSchema = new schema.Entity('forum.contestthread', {
    photo: PhotoSchema,
});

export const BrandDaysCampaignSchema = new schema.Entity('brandDaysCampaigns', {
    user: UserSchema,
});

export const CounsellingSchema = new schema.Entity('forum.counselling', {
    topic: TopicSchema,
    counsellor: UserSchema,
    counsellorAvatarPhoto: PhotoSchema,
});

export const ArchivedCampaignsSchema = new schema.Entity('archivedCampaigns', {
    topic: TopicSchema,
});

export interface MessageEntity extends CommentEntity {
    messagePhotos: MessagePhotoEntity[];
}

// TODO Refactor, because it's confusing for most of us, it's like Comment Schema
// TODO but because Like needs entity matched with DB, we need to write new schema
export const MessageSchema = new schema.Entity('forum.message', {
    authorUser: UserSchema,
    targetOwner: UserSchema,
    photos: [PhotoSchema],
    messagePhotos: [MessagePhotoSchema],
    likeable: createLikeableSchema('forum-message'),
    deletedBy: UserSchema,
});

export interface ComplaintsStatistics {
    count: number;
    topicId: number;
}
export interface ComplaintsStatisticsPerPost {
    [postId: number]: ComplaintsStatistics;
}
export interface ComplaintsCountPerTopic {
    [topicId: number]: number;
}

MessagePhotoSchema.define({
    photofile: PhotoSchema,
    message: MessageSchema,
});
